import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import EditorialPostCard from '../../molecules/EditorialPostCard';
import UserCardAvatar from '../../molecules/UserCardAvatar';
import { MessageIcon } from '../../atoms/icons/FriendshipActionIcons';
import classNames from '../../../utils/classNames';
import { buildProfileSharePath } from '../../../utils/profileRoutes';
import {
    formatSeekingSummary,
    formatSeekingTargetPhrase,
} from '../../../utils/seeking';
import { startDirectMessageSession } from '../../../utils/directMessageNavigation';
import './index.scss';

const STRUCTURED_SEEKING_PATTERN = /\{\s*"type"\s*:/;

function readText(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function normalizeUserId(value) {
    return readText(value).toLowerCase();
}

function containsStructuredSeeking(value) {
    return STRUCTURED_SEEKING_PATTERN.test(readText(value));
}

function extractStructuredSeeking(value) {
    const text = readText(value);
    const start = text.indexOf('{');
    const end = text.lastIndexOf('}');
    return start >= 0 && end > start ? text.slice(start, end + 1) : '';
}

function resolveSeekingTargetPhrase(item) {
    return formatSeekingTargetPhrase(item?.profile?.seeking)
        || formatSeekingTargetPhrase(extractStructuredSeeking(item?.headline))
        || formatSeekingTargetPhrase(extractStructuredSeeking(item?.body));
}

function resolveHeadline(item, target, t) {
    const rawHeadline = readText(item?.headline);
    if (rawHeadline && !containsStructuredSeeking(rawHeadline)) {
        return rawHeadline;
    }

    const seekingPhrase = resolveSeekingTargetPhrase(item);
    if (seekingPhrase) {
        return t('home.suggestedUserCard.lookingForHeadline', {
            defaultValue: '{{name}} is looking for {{seeking}}',
            name: target.displayName,
            seeking: seekingPhrase,
        });
    }

    return t('home.suggestedUserCard.fallbackHeadline', {
        defaultValue: '{{name}} could be a match',
        name: target.displayName,
    });
}

function resolveBody(item, t) {
    const rawBody = readText(item?.body);
    if (rawBody && !containsStructuredSeeking(rawBody)) {
        return rawBody;
    }

    const seekingSummary = formatSeekingSummary(item?.profile?.seeking, { t })
        || formatSeekingSummary(extractStructuredSeeking(rawBody), { t });
    if (seekingSummary) {
        return seekingSummary;
    }

    return t('home.suggestedUserCard.fallbackBody', {
        defaultValue: 'Their profile lines up with your instruments, style, or search.',
    });
}

function resolveTargetUser(item) {
    const user = item?.user && typeof item.user === 'object' ? item.user : {};
    const profile = item?.profile && typeof item.profile === 'object' ? item.profile : {};
    const userId = readText(item?.targetUserId)
        || readText(user.userId)
        || readText(user.id)
        || readText(profile.userId);
    const username = readText(user.username) || readText(profile.username);
    const displayName = readText(user.displayName)
        || readText(profile.displayName)
        || username
        || userId;
    const avatar = readText(user.avatar) || readText(profile.avatar);
    return { userId, username, displayName, avatar };
}

function resolveInstrumentLabels(item) {
    const matched = Array.isArray(item?.matchedInstruments) ? item.matchedInstruments : [];
    return matched
        .map((instrument) => {
            const name = readText(instrument?.instrument);
            const level = readText(instrument?.level);
            return name ? { name, level, primary: instrument?.primary === true } : null;
        })
        .filter(Boolean)
        .slice(0, 3);
}

function resolveReasonLabels(item) {
    const reasons = Array.isArray(item?.matchReasons) ? item.matchReasons : [];
    return reasons
        .map((reason) => {
            const rawReason = readText(reason);
            if (!containsStructuredSeeking(rawReason)) {
                return rawReason;
            }
            const seekingPhrase = formatSeekingTargetPhrase(extractStructuredSeeking(rawReason))
                || resolveSeekingTargetPhrase(item);
            if (!seekingPhrase) {
                return '';
            }
            return /^looking\s+for/i.test(rawReason)
                ? `Looking for ${seekingPhrase}`
                : seekingPhrase;
        })
        .filter(Boolean)
        .slice(0, 3);
}

function resolveLocationLabel(item) {
    const profile = item?.profile && typeof item.profile === 'object' ? item.profile : {};
    return readText(profile.city)
        || readText(profile.locationFormatted)
        || readText(item?.distanceBand).replace(/_/g, ' ').toLowerCase();
}

export default function SuggestedUserCard({
    item,
    readOnly = false,
    currentUserId = null,
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const mountedRef = useRef(true);
    const [isMessageInFlight, setIsMessageInFlight] = useState(false);
    const target = useMemo(() => resolveTargetUser(item), [item]);
    const instruments = useMemo(() => resolveInstrumentLabels(item), [item]);
    const profilePath = useMemo(
        () => buildProfileSharePath({ userId: target.userId, username: target.username }),
        [target.userId, target.username],
    );
    const headline = useMemo(() => resolveHeadline(item, target, t), [item, target, t]);
    const reasons = useMemo(() => resolveReasonLabels(item), [item]);
    const body = useMemo(() => resolveBody(item, t), [item, t]);
    const locationLabel = resolveLocationLabel(item);
    const distanceMiles = Number.isFinite(item?.distanceMiles) ? item.distanceMiles : null;
    const normalizedCurrentUserId = normalizeUserId(currentUserId);
    const normalizedTargetUserId = normalizeUserId(target.userId);
    const canViewProfile = !readOnly && Boolean(profilePath);
    const canMessage = !readOnly
        && Boolean(normalizedCurrentUserId)
        && Boolean(normalizedTargetUserId)
        && normalizedCurrentUserId !== normalizedTargetUserId;
    const messageLabel = isMessageInFlight
        ? t('friendsPage.userList.actions.opening', { defaultValue: 'Opening...' })
        : t('friendsPage.userList.actions.message', { defaultValue: 'Message' });

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const openProfile = useCallback(() => {
        if (profilePath) {
            navigate(profilePath);
        }
    }, [navigate, profilePath]);

    const openDirectMessage = useCallback(async () => {
        if (!canMessage || isMessageInFlight || !target.userId) {
            return;
        }

        setIsMessageInFlight(true);
        try {
            await startDirectMessageSession(target.userId, {
                currentUserId,
            });
        } catch (error) {
            console.error('Failed to open direct message from suggestion card.', error);
        } finally {
            if (mountedRef.current) {
                setIsMessageInFlight(false);
            }
        }
    }, [canMessage, currentUserId, isMessageInFlight, target.userId]);

    const matchMetaNode = (
        <div className="suggested-user-card__meta-row suggested-user-card__match-meta">
            <span>{t('home.suggestedUserCard.badge', { defaultValue: 'Suggested match' })}</span>
            {locationLabel ? (
                <>
                    <span className="suggested-user-card__meta-dot" aria-hidden="true" />
                    <span>{locationLabel}</span>
                </>
            ) : null}
            {distanceMiles != null ? (
                <>
                    <span className="suggested-user-card__meta-dot" aria-hidden="true" />
                    <span>{distanceMiles.toFixed(1)} mi</span>
                </>
            ) : null}
        </div>
    );

    const hasActions = canViewProfile || canMessage;
    // The match meta (Suggested match • city • distance) lives in the card footer,
    // aligned to the left opposite the actions, so the header stays focused on the
    // person's name and headline.
    const footerNode = (
        <>
            {matchMetaNode}
            {hasActions ? (
                <div className="suggested-user-card__actions">
                    {canViewProfile ? (
                        <button type="button" className="suggested-user-card__action" onClick={openProfile}>
                            {t('home.suggestedUserCard.viewProfile', { defaultValue: 'View profile' })}
                        </button>
                    ) : null}
                    {canMessage ? (
                        <button
                            type="button"
                            className="suggested-user-card__action"
                            onClick={openDirectMessage}
                            disabled={isMessageInFlight}
                            aria-busy={isMessageInFlight || undefined}
                        >
                            <MessageIcon width="16" height="16" />
                            {messageLabel}
                        </button>
                    ) : null}
                </div>
            ) : null}
        </>
    );

    return (
        <EditorialPostCard
            variant="text"
            className={classNames('suggested-user-card', {
                'suggested-user-card--read-only': readOnly,
            })}
            footer={footerNode}
        >
            <div className="suggested-user-card__profile">
                <UserCardAvatar
                    userId={target.userId}
                    username={target.username}
                    avatarUrl={target.avatar}
                    alt={target.displayName}
                    className="suggested-user-card__avatar"
                    radius="18%"
                />
                <div className="suggested-user-card__main">
                    <div className="suggested-user-card__identity">
                        <div className="suggested-user-card__identity-row">
                            <span className="suggested-user-card__name">{target.displayName}</span>
                            <span className="suggested-user-card__badge">
                                {t('home.postCard.badges.suggestedForYou', { defaultValue: 'Suggested for you' })}
                            </span>
                        </div>
                    </div>

                    <div className="suggested-user-card__content">
                        <h3 className="suggested-user-card__headline">{headline}</h3>
                        <p className="suggested-user-card__body">{body}</p>
                    </div>
                </div>
            </div>

            {instruments.length > 0 ? (
                <div className="suggested-user-card__chips" aria-label={t('home.suggestedUserCard.instruments', { defaultValue: 'Matched instruments' })}>
                    {instruments.map((instrument) => (
                        <span
                            className={classNames('suggested-user-card__chip', {
                                'suggested-user-card__chip--primary': instrument.primary,
                            })}
                            key={`${instrument.name}:${instrument.level}`}
                        >
                            <strong>{instrument.name}</strong>
                            {instrument.level ? <small>{instrument.level}</small> : null}
                        </span>
                    ))}
                </div>
            ) : null}

            {reasons.length > 0 ? (
                <ul className="suggested-user-card__reasons" aria-label={t('home.suggestedUserCard.reasons', { defaultValue: 'Match reasons' })}>
                    {reasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                    ))}
                </ul>
            ) : null}
        </EditorialPostCard>
    );
}

SuggestedUserCard.propTypes = {
    item: PropTypes.shape({
        targetUserId: PropTypes.string,
        headline: PropTypes.string,
        body: PropTypes.string,
        distanceMiles: PropTypes.number,
        distanceBand: PropTypes.string,
        matchReasons: PropTypes.arrayOf(PropTypes.string),
        matchedInstruments: PropTypes.arrayOf(PropTypes.shape({
            instrument: PropTypes.string,
            level: PropTypes.string,
            primary: PropTypes.bool,
        })),
        user: PropTypes.shape({
            id: PropTypes.string,
            userId: PropTypes.string,
            username: PropTypes.string,
            displayName: PropTypes.string,
            avatar: PropTypes.string,
        }),
        profile: PropTypes.shape({
            userId: PropTypes.string,
            username: PropTypes.string,
            displayName: PropTypes.string,
            avatar: PropTypes.string,
            seeking: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            city: PropTypes.string,
            locationFormatted: PropTypes.string,
        }),
    }).isRequired,
    readOnly: PropTypes.bool,
    currentUserId: PropTypes.string,
};
